import { getWebInstrumentations, initializeFaro, type Faro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

/** Grafana Faro RUM configuration */
export interface FaroConfig {
  appName: string;
  appVersion: string;
  environment: string;
  enabled: boolean;
}

let faroInstance: Faro | null = null;

/** Loads Faro config from runtime config.json or environment variables */
export async function loadFaroConfig(): Promise<FaroConfig> {
  try {
    const response = await fetch('/config.json');
    if (response.ok) {
      const config = await response.json();
      return {
        appName: config.faroAppName || 'bol-backoffice',
        appVersion: config.faroAppVersion || '1.0.0',
        environment: config.faroEnvironment || 'production',
        enabled: config.faroEnabled !== 'false',
      };
    }
  } catch {
    console.log('⚠️ Failed to load Faro config from /config.json, using environment variables');
  }

  return {
    appName: import.meta.env.VITE_FARO_APP_NAME || 'bol-backoffice',
    appVersion: import.meta.env.VITE_FARO_APP_VERSION || import.meta.env.VITE_APP_VERSION || '1.0.0',
    environment: import.meta.env.VITE_FARO_ENVIRONMENT || import.meta.env.MODE || 'dev',
    enabled: import.meta.env.VITE_FARO_ENABLED !== 'false',
  };
}

/**
 * Initializes Grafana Faro RUM with error tracking, performance monitoring,
 * session tracking, and distributed tracing. Proxied through nginx at /grafana-faro.
 */
export async function initializeFaroRUM(config?: FaroConfig): Promise<Faro | null> {
  if (faroInstance) {
    return faroInstance;
  }

  try {
    const faroConfig = config || await loadFaroConfig();

    if (!faroConfig.enabled) {
      console.log('ℹ️ Grafana Faro is disabled');
      return null;
    }

    console.log(`🔍 Initializing Grafana Faro RUM for ${faroConfig.environment}...`);

    faroInstance = initializeFaro({
      url: '/grafana-faro',
      app: {
        name: faroConfig.appName,
        version: faroConfig.appVersion,
        environment: faroConfig.environment,
      },

      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: faroConfig.environment === 'production',
        }),
        new TracingInstrumentation({
          instrumentationOptions: {
            // Propagate trace headers to backend APIs for distributed tracing
            propagateTraceHeaderCorsUrls: [
              /\/bol-api\/.*/,
              /\/notifications-api\/.*/,
            ],
          },
        }),
      ],

      sessionTracking: {
        enabled: true,
        persistent: true,
      },

      batching: {
        enabled: true,
        sendTimeout: 5000,
      },

      ignoreUrls: [],

      beforeSend: (event) => {
        return event;
      },
    });

    console.log('✅ Grafana Faro initialized successfully');
    return faroInstance;
  } catch (error) {
    console.error('❌ Failed to initialize Grafana Faro:', error);
    return null;
  }
}

/** Returns the current Faro instance */
export function getFaro(): Faro | null {
  return faroInstance;
}

/** Tracks custom errors with optional context */
export function pushError(error: Error, context?: Record<string, unknown>): void {
  if (faroInstance) {
    faroInstance.api.pushError(error, context);
  }
}

/** Tracks custom events for business metrics and user actions */
export function pushEvent(name: string, attributes?: Record<string, string>): void {
  if (faroInstance) {
    faroInstance.api.pushEvent(name, attributes);
  }
}

/** Sends log messages to Faro */
export function pushLog(
  message: string, 
  level: 'log' | 'info' | 'warn' | 'error' = 'info',
  context?: Record<string, string>
): void {
  if (faroInstance) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    faroInstance.api.pushLog([message], { level: level as any, context: context as any });
  }
}

/** Sets user information for session tracking (call after authentication) */
export function setUser(userId: string, attributes?: Record<string, string>): void {
  if (faroInstance) {
    faroInstance.api.setUser({
      id: userId,
      ...attributes,
    });
  }
}

/** Gets OpenTelemetry APIs for manual tracing */
export function getOTEL() {
  if (!faroInstance) {
    throw new Error('Faro not initialized. Call initializeFaroRUM() first.');
  }
  const otel = faroInstance.api.getOTEL();
  if (!otel?.trace || !otel?.context) {
    throw new Error('OpenTelemetry tracing is not available. Ensure TracingInstrumentation is configured.');
  }
  return otel;
}

/** Executes a function within a traced span */
export async function traceOperation<T>(
  name: string,
  fn: () => T | Promise<T>,
  attributes?: Record<string, string | number | boolean>
): Promise<T> {
  if (!faroInstance) {
    return fn();
  }

  const otel = faroInstance.api.getOTEL();
  if (!otel?.trace || !otel?.context) {
    return fn();
  }

  const { trace, context } = otel;
  const tracer = trace.getTracer('bol-backoffice');
  const span = tracer.startSpan(name);

  if (attributes) {
    span.setAttributes(attributes);
  }

  try {
    const result = await context.with(trace.setSpan(context.active(), span), async () => {
      return await Promise.resolve(fn());
    });
    span.end();
    return result;
  } catch (error) {
    span.recordException(error as Error);
    span.end();
    throw error;
  }
}

/** Clears user information (call on logout) */
export function clearUser(): void {
  if (faroInstance) {
    faroInstance.api.setUser(undefined);
  }
}
